import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers, resolveComponent as _resolveComponent, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "max-h-full bg-white p-5 rounded overflow-y-auto w-full pointer-events-auto shadow-black" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { class: "flex gap-2 items-center" }
const _hoisted_4 = { class: "block" }
const _hoisted_5 = ["onClick"]
const _hoisted_6 = { class: "flex gap-2 items-center" }
const _hoisted_7 = { class: "block" }
const _hoisted_8 = ["onClick"]
const _hoisted_9 = ["href"]
const _hoisted_10 = ["src", "alt"]

import {
  computed,
  inject, nextTick,
  onMounted,
  PropType,
  reactive, Ref,
  ref,
  watch,
} from 'vue';
import OpenSeadragon from 'openseadragon';
import { cloneDeep, orderBy } from 'lodash';
import { useI18n } from 'vue-i18n';
import { useQueryClient } from 'vue-query';
import { useRoute, useRouter } from 'vue-router';
import { ElNotification } from 'element-plus';
import { InfoFilled } from '@element-plus/icons-vue';
import { AxiosError } from 'axios';
import { useGetInspectionGroupQuery } from '@/entities/inspection-group/hooks';
import { getDomainPath } from '@/services';

import tokens from '@/styles/tokens';

import {
  annotationGroupsQueryName,
  useDeleteAnnotationGroupMutation,
  useGetAnnotationGroupsQuery,
} from '@/entities/annotation-group/hooks';

import {
  useGetAnnotationImagesQuery,
  useUploadAnnotationImageMutation,
} from '@/entities/annotation-image/hooks';

import {
  annotationsQueryName,
  useDeleteAnnotationMutation,
  useGetAnnotationQuery,
  useGetAnnotationsQuery,
  useUpdateAnnotationMutation,
} from '@/entities/annotation/hooks';

import { useForm } from '@/core/hooks/forms';

import { Device } from '@/entities/device/types';
import { Inspection } from '@/entities/inspection/types';
import { SymfonyError } from '@/types/services';
import { Annotation } from '@/entities/annotation/types';
import { FormExposition } from '@/core/components/forms/Form/form.shared';
import { InspectionGroup } from '@/entities/inspection-group/types';
import {
  InspectionViewerInjection,
  inspectionViewerInjectionKey,
} from '@/teams/visionair/components/InspectionViewer/inspection-viewer.shared';

import {
  NkIcon,
  NkButton,
  NkForm,
  NkFormItem,
  NkGroup,
  NkStack,
  NkTextArea,
  NkBadge,
  NkFilePond,
  NkGallery,
  NkHeading,
  NkModal,
  NkSelect,
  NkOption,
} from '@/core/components';

import AnnotationTypeSelect
  from '@/entities/annotation-type/components/inputs/AnnotationTypeSelect.vue';
import AnnotationSeveritySelect
  from '@/entities/annotation-severity/components/inputs/AnnotationSeveritySelect.vue';
import { useGetAnnotationTypesQuery } from '@/entities/annotation-type/hooks';
import AnnotationHistoryViewer
  from '@/teams/visionair/components/InspectionViewer/AnnotationHistoryViewer.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'InspectionViewerUpdateAnnotationForm',
  props: {
  annotation: {
    type: Number as PropType<Annotation['id']>,
    required: true,
  },
  inspection: {
    type: Number as PropType<Inspection['id']>,
    required: true,
  },
  inspectionGroup: {
    type: Number as PropType<InspectionGroup['id']>,
    required: true,
  },
  device: {
    type: Number as PropType<Device['id']>,
    required: true,
  },
  location: {
    type: Object as PropType<OpenSeadragon.Rect>,
    required: true,
  },
  splitted: {
    type: Boolean,
    default: false,
  },
},
  emits: ['cancel', 'submitted'],
  setup(__props, { emit }) {

const props = __props

const inspectionViewerInjection = inject(inspectionViewerInjectionKey) as InspectionViewerInjection;



const router = useRouter();
const route = useRoute();
const queryClient = useQueryClient();
const { t } = useI18n();


const { onSubmitted } = useForm(emit);

const form = ref<FormExposition | undefined>();
const annotationGallery = ref();
const annotationFileDropzone = ref<typeof NkFilePond>();
const annotationHistoryOpen = ref(false);

const fields = reactive<Annotation>({} as unknown as Annotation);
const rules = {};

// Get current annotation
const annotationQueryData = computed(() => ({ id: props.annotation }));
const hasAnnotationQueryData = computed(() => !!annotationQueryData.value.id);
const {
  data: annotation,
  isLoading: annotationLoading,
} = useGetAnnotationQuery(annotationQueryData, {
  enabled: hasAnnotationQueryData,
  refetchOnWindowFocus: false,
});

// Get current annotation image
const annotationImagesIds = computed(() => annotation.value?.annotationImages || []);
const hasAnnotationImagesIds = computed(() => !!annotationImagesIds.value.length);
const annotationImagesQueryFilters = computed(() => ({ ids: [...annotationImagesIds.value] }));
const { data: annotationImages } = useGetAnnotationImagesQuery({
  refetchOnWindowFocus: false,
  enabled: hasAnnotationImagesIds,
  onSuccess: () => {
    nextTick(() => {
      annotationGallery.value?.instance?.refresh();
    });
  },
}, annotationImagesQueryFilters);

// Get annotation groups (normal + history)
const annotationGroupQueryFilters: Ref = computed(() => ({ ids: (annotation.value?.annotationGroups || []) }));
const hasAnnotationGroupQueryFilters = computed(() => !!annotationGroupQueryFilters.value.ids?.length);
const { data: annotationGroups } = useGetAnnotationGroupsQuery({
  enabled: hasAnnotationGroupQueryFilters,
}, annotationGroupQueryFilters);

// Get normal group annotations
const normaLinkedAnnotationsFilters = computed(() => ({
  ids: annotationGroups.value?.find(o => o.type === 'normal')?.annotations?.filter(annotationId => annotationId !== annotation.value?.id) || [],
}));
const hasNormaLinkedAnnotationsFilters = computed(() => !!normaLinkedAnnotationsFilters.value.ids?.length);
const { data: normalLinkedAnnotations } = useGetAnnotationsQuery({
  enabled: hasNormaLinkedAnnotationsFilters,
}, normaLinkedAnnotationsFilters);

// Get history group annotations
const historyLinkedAnnotationsFilters = computed(() => ({
  ids: annotationGroups.value?.find(o => o.type === 'history')?.annotations?.filter(annotationId => annotationId !== annotation.value?.id) || [],
}));
const hasHistoryLinkedAnnotationsFilters = computed(() => !!historyLinkedAnnotationsFilters.value.ids?.length);
const { data: historyLinkedAnnotations } = useGetAnnotationsQuery({
  enabled: hasHistoryLinkedAnnotationsFilters,
}, historyLinkedAnnotationsFilters);

const inspectionGroupQueryData = computed(() => ({ id: props.inspectionGroup }));
const hasInspectionGroupQueryData = computed(() => !!inspectionGroupQueryData.value.id);
const { data: inspectionGroupData } = useGetInspectionGroupQuery(inspectionGroupQueryData, { enabled: hasInspectionGroupQueryData });

const annotationTypesQueryFilters: Ref = computed(() => ({ organizations: 0, itemsPerPage: 50 }));
const { data: annotationTypesData } = useGetAnnotationTypesQuery({
  refetchOnWindowFocus: false,
}, annotationTypesQueryFilters);
const annotationTypes = computed(() => orderBy((annotationTypesData.value || []), ['order', 'name']));

// Mutations
const {
  mutateAsync: updateAnnotationMutation,
  isLoading: updateAnnotationIsLoading,
} = useUpdateAnnotationMutation();
const {
  mutateAsync: deleteAnnotationGroupMutation,
  isLoading: deleteAnnotationGroupLoading,
} = useDeleteAnnotationGroupMutation();

const {
  mutateAsync: annotationDeleteMutation,
  isLoading: annotationDeleteLoading,
} = useDeleteAnnotationMutation();

const {
  mutateAsync: uploadAnnotationImage,
  error: uploadAnnotationImageError,
  isError: uploadAnnotationImageIsError,
  isSuccess: uploadAnnotationImageIsSuccess,
} = useUploadAnnotationImageMutation();

const assignFields = () => {
  if (annotation.value) {
    const annotationClone = cloneDeep<Annotation>(annotation.value);
    Object.assign(fields, annotationClone);
  }
};

const save = () => {
  if (!updateAnnotationIsLoading.value) {
    form.value?.validator.$validate()
      .then((valid: boolean) => {
        if (valid) {
          updateAnnotationMutation({
            ...fields,
          },
          {
            onSuccess: () => {
              ElNotification({
                title: t('annotation.notification.edit.success.title'),
                message: t('annotation.notification.edit.success.message'),
                type: 'success',
                position: 'bottom-right',
              });
            }
          }

          ).then(() => {
            onSubmitted();
          }).catch(() => {
            console.log('Error on annotation update');
          });
        }
      });
  }
};

const removeAnnotationLink = async (linkedAnnotation: Annotation, groupType: 'normal' | 'history') => {
  const group = annotationGroups.value?.find((item) => item.type === groupType);

  if (!group || !annotation.value || deleteAnnotationGroupLoading.value || updateAnnotationIsLoading.value) {
    return;
  }

  if ((group.annotations?.length || 0) <= 2) {
    await deleteAnnotationGroupMutation({ id: group.id });
  } else {
    await updateAnnotationMutation({
      ...linkedAnnotation,
      annotationGroups: linkedAnnotation.annotationGroups.filter((groupId) => groupId !== group.id),
    });
  }

  await queryClient.refetchQueries(annotationGroupsQueryName, { active: true });
  await queryClient.refetchQueries([annotationsQueryName, { id: annotation.value.id }]);
};

const handleDelete = () => {
  if (!annotationDeleteLoading.value) {
    annotationDeleteMutation({ id: props.annotation }, {
      onSuccess: async () => {
        await emit('cancel');
        ElNotification({
          type: 'success',
          position: 'bottom-right',
          message: t('annotation.notification.delete.success'),
        });
      },
      onError: (err: AxiosError<SymfonyError, SymfonyError>) => {
        ElNotification({
          type: 'error',
          position: 'bottom-right',
          message: err?.response?.data?.title || t('notification.error'),
        });
      },
    });
  }
};

const centerView = () => {
  inspectionViewerInjection.centerFormView(props.location);
};

const openGallery = () => {
  annotationGallery.value.instance.openGallery(0);
};

const openHistory = () => {
  annotationHistoryOpen.value = true;
}

const closeHistory = () => {
  annotationHistoryOpen.value = false;
}

const goToAnnotationInspection = (a: Annotation) => {
  const { position } = a;

  if (a.inspection !== annotation.value?.inspection) {
    return router.push({
      name: 'visionair-inspection',
      params: {
        inspection: a.inspection,
      },
      query: {
        view: route.query.view,
        'highlight-annotation': a.id,
      },
    });
  }

  if (a && position) {
    return inspectionViewerInjection.setActiveAnnotation({
      id: a.id,
      location: new OpenSeadragon.Rect(
        position.viewportX,
        position.viewportY,
        position.viewportWidth,
        position.viewportHeight,
      ),
    });
  }

  return false;
};

watch(() => props.location, () => {
  centerView();
});

watch(annotation, () => {
  assignFields();
});


onMounted(() => {
  assignFields();
  centerView();

  nextTick(() => {
    annotationFileDropzone.value?.filePond._pond.setOptions({
      server: {
        process: (fieldName, file) => {
          const formData = new FormData();

          formData.append('files[]', file);

          uploadAnnotationImage({
            id: props.annotation,
            files: formData,
          }, {
            onSuccess: async () => {
              await queryClient.refetchQueries([annotationsQueryName, { id: annotation.value.id }]);
            },
          }).finally(() => {
            annotationFileDropzone.value?.filePond.removeFiles();
          });
        },
      },
    });
  });

});

return (_ctx: any,_cache: any) => {
  const _component_ElAlert = _resolveComponent("ElAlert")!
  const _component_ElPopconfirm = _resolveComponent("ElPopconfirm")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["z-50 absolute top-0 py-5 w-screen h-full max-w-[400px] flex items-end pointer-events-none", {
      'right-0': props.splitted,
      '!left-1/2': !props.splitted
    }])
  }, [
    _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
      _createVNode(_unref(NkStack), { spacing: 5 }, {
        default: _withCtx(() => [
          _createVNode(_unref(NkGroup), { position: "apart" }, {
            default: _withCtx(() => [
              _createVNode(_unref(NkGroup), { spacing: 4 }, {
                default: _withCtx(() => [
                  _createVNode(_unref(NkButton), {
                    title: "Recentrer",
                    circle: "",
                    icon: "NkVisibility",
                    type: "white",
                    onClick: centerView
                  }),
                  (_unref(annotationImages) && _unref(annotationImages).length && _ctx.$store.getters['connection/isGranted'](['annotation_image_view']))
                    ? _withDirectives((_openBlock(), _createBlock(_unref(NkButton), {
                        key: 0,
                        disabled: _unref(annotationLoading),
                        title: "Voir les images annexes",
                        circle: "",
                        icon: "NkPermMedia",
                        type: "white",
                        onClick: openGallery
                      }, null, 8, ["disabled"])), [
                        [_directive_loading, _unref(annotationLoading)]
                      ])
                    : _createCommentVNode("", true),
                  (_ctx.$store.getters['connection/isGranted'](['ROLE_ADMIN']))
                    ? _withDirectives((_openBlock(), _createBlock(_unref(NkButton), {
                        key: 1,
                        disabled: _unref(annotationLoading),
                        title: "Voir l'historique de l'annotation",
                        circle: "",
                        icon: "NkHistory",
                        type: "white",
                        onClick: openHistory
                      }, null, 8, ["disabled"])), [
                        [_directive_loading, _unref(annotationLoading)]
                      ])
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }),
              _createVNode(_unref(NkButton), {
                circle: "",
                icon: "NkClose",
                type: "white",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('cancel')))
              })
            ]),
            _: 1
          }),
          _createVNode(_unref(NkHeading), { type: "small-italic" }, {
            default: _withCtx(() => [
              _createTextVNode(" ID: " + _toDisplayString(_unref(annotation).idForReport ?? 'Pending') + " ", 1),
              (_ctx.$store.getters['connection/isGranted'](['ROLE_ADMIN']))
                ? (_openBlock(), _createElementBlock("span", _hoisted_2, "(" + _toDisplayString(props.annotation) + ")", 1))
                : _createCommentVNode("", true)
            ]),
            _: 1
          }),
          _createElementVNode("label", null, "Hub distance: " + _toDisplayString(_unref(annotation)?.distanceFromHub), 1),
          _createElementVNode("label", null, "Defect size: " + _toDisplayString(_unref(annotation)?.defectSize), 1),
          (!!Object.keys(fields).length)
            ? (_openBlock(), _createBlock(_unref(NkForm), {
                key: 0,
                ref_key: "form",
                ref: form,
                fields: fields,
                rules: rules
              }, {
                footer: _withCtx(() => [
                  _createVNode(_unref(NkGroup), { position: "apart" }, {
                    default: _withCtx(() => [
                      _createVNode(_component_ElPopconfirm, {
                        "confirm-button-text": "OK",
                        "cancel-button-text": _ctx.$t('actions.cancel'),
                        icon: _unref(InfoFilled),
                        "icon-color": _unref(tokens).colors.primary.DEFAULT,
                        title: _ctx.$t('annotation.action.delete_confirm'),
                        onConfirm: handleDelete
                      }, {
                        reference: _withCtx(() => [
                          (_ctx.$store.getters['connection/isGranted'](['annotation_delete']))
                            ? _withDirectives((_openBlock(), _createBlock(_unref(NkButton), {
                                key: 0,
                                type: "danger"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.$t('actions.delete')), 1)
                                ]),
                                _: 1
                              })), [
                                [_directive_loading, _unref(annotationDeleteLoading)]
                              ])
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }, 8, ["cancel-button-text", "icon", "icon-color", "title"]),
                      (_ctx.$store.getters['connection/isGranted'](['annotation_create','annotation_edit']))
                        ? (_openBlock(), _createBlock(_unref(NkButton), {
                            key: 0,
                            disabled: _unref(updateAnnotationIsLoading),
                            onClick: save
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_ctx.$t('actions.save')), 1)
                            ]),
                            _: 1
                          }, 8, ["disabled"]))
                        : _createCommentVNode("", true)
                    ]),
                    _: 1
                  })
                ]),
                default: _withCtx(() => [
                  (_unref(normalLinkedAnnotations)?.length)
                    ? (_openBlock(), _createBlock(_unref(NkFormItem), {
                        key: 0,
                        label: _ctx.$t('misc.related_annotations')
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(NkGroup), { spacing: 2 }, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(normalLinkedAnnotations), (item) => {
                                return (_openBlock(), _createBlock(_unref(NkBadge), {
                                  key: item.id,
                                  plain: "",
                                  size: "sm",
                                  class: "cursor-pointer hover:opacity-80",
                                  onClick: ($event: any) => (goToAnnotationInspection(item))
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_3, [
                                      _createVNode(_unref(NkIcon), {
                                        size: 3,
                                        name: item.inspection.id === _unref(annotation)?.inspection ? 'NkRoom' : 'NkLogin'
                                      }, null, 8, ["name"]),
                                      _createElementVNode("span", _hoisted_4, " Annotation " + _toDisplayString(item.id), 1),
                                      _createElementVNode("button", {
                                        type: "button",
                                        class: "ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full border border-red-500 bg-red-50 px-1 text-[11px] font-bold leading-none text-red-500 transition-colors hover:bg-red-500 hover:text-white",
                                        title: "Remove link",
                                        onClick: _withModifiers(($event: any) => (removeAnnotationLink(item, 'normal')), ["stop"])
                                      }, " x ", 8, _hoisted_5)
                                    ])
                                  ]),
                                  _: 2
                                }, 1032, ["onClick"]))
                              }), 128))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["label"]))
                    : _createCommentVNode("", true),
                  (_unref(historyLinkedAnnotations)?.length)
                    ? (_openBlock(), _createBlock(_unref(NkFormItem), {
                        key: 1,
                        label: _ctx.$t('misc.history')
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(NkGroup), { spacing: 2 }, {
                            default: _withCtx(() => [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(historyLinkedAnnotations), (item) => {
                                return (_openBlock(), _createBlock(_unref(NkBadge), {
                                  key: item.id,
                                  plain: "",
                                  type: "info",
                                  size: "sm",
                                  class: "cursor-pointer hover:opacity-80",
                                  onClick: ($event: any) => (goToAnnotationInspection(item))
                                }, {
                                  default: _withCtx(() => [
                                    _createElementVNode("div", _hoisted_6, [
                                      _createVNode(_unref(NkIcon), {
                                        size: 3,
                                        name: item.inspection.id === _unref(annotation)?.inspection ? 'NkRoom' : 'NkLogin'
                                      }, null, 8, ["name"]),
                                      _createElementVNode("span", _hoisted_7, " Annotation " + _toDisplayString(item.id), 1),
                                      _createElementVNode("button", {
                                        type: "button",
                                        class: "ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full border border-red-500 bg-red-50 px-1 text-[11px] font-bold leading-none text-red-500 transition-colors hover:bg-red-500 hover:text-white",
                                        title: "Remove history link",
                                        onClick: _withModifiers(($event: any) => (removeAnnotationLink(item, 'history')), ["stop"])
                                      }, " x ", 8, _hoisted_8)
                                    ])
                                  ]),
                                  _: 2
                                }, 1032, ["onClick"]))
                              }), 128))
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }, 8, ["label"]))
                    : _createCommentVNode("", true),
                  _createVNode(_unref(NkFormItem), {
                    label: _ctx.$t('misc.type'),
                    prop: "type"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NkSelect), {
                        modelValue: fields.type,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((fields.type) = $event))
                      }, {
                        default: _withCtx(() => [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(annotationTypes), (type) => {
                            return (_openBlock(), _createBlock(_unref(NkOption), {
                              key: type.id,
                              value: type.id,
                              label: type.name
                            }, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(type.name), 1)
                              ]),
                              _: 2
                            }, 1032, ["value", "label"]))
                          }), 128))
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_unref(NkFormItem), {
                    label: _ctx.$t('misc.severity'),
                    prop: "severity"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(AnnotationSeveritySelect, {
                        service: _unref(inspectionGroupData)?.service,
                        modelValue: fields.severity,
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((fields.severity) = $event))
                      }, null, 8, ["service", "modelValue"])
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_unref(NkFormItem), {
                    label: _ctx.$t('misc.status'),
                    prop: "status"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NkSelect), {
                        modelValue: fields.status,
                        "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((fields.status) = $event))
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_unref(NkOption), {
                            value: "to fix",
                            label: "To Fix"
                          }),
                          _createVNode(_unref(NkOption), {
                            value: "fixed",
                            label: "Fixed"
                          })
                        ]),
                        _: 1
                      }, 8, ["modelValue"])
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  _createVNode(_unref(NkFormItem), {
                    label: _ctx.$t('misc.comment'),
                    prop: "comment"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_unref(NkTextArea), {
                        modelValue: fields.comment,
                        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((fields.comment) = $event))
                      }, null, 8, ["modelValue"])
                    ]),
                    _: 1
                  }, 8, ["label"]),
                  (_ctx.$store.getters['connection/isGranted'](['annotation_image_create']))
                    ? (_openBlock(), _createBlock(_unref(NkFormItem), {
                        key: 2,
                        label: _ctx.$t('annotation.form.files.label'),
                        prop: "comment"
                      }, {
                        default: _withCtx(() => [
                          (_unref(uploadAnnotationImageIsError))
                            ? (_openBlock(), _createBlock(_component_ElAlert, {
                                key: 0,
                                title: _unref(uploadAnnotationImageError),
                                type: "error",
                                "show-icon": ""
                              }, null, 8, ["title"]))
                            : _createCommentVNode("", true),
                          (_unref(uploadAnnotationImageIsSuccess))
                            ? (_openBlock(), _createBlock(_component_ElAlert, {
                                key: 1,
                                title: _ctx.$t('notification.file_upload_success'),
                                type: "success",
                                "show-icon": ""
                              }, null, 8, ["title"]))
                            : _createCommentVNode("", true),
                          _createVNode(_unref(NkFilePond), {
                            ref_key: "annotationFileDropzone",
                            ref: annotationFileDropzone,
                            options: {
                allowImagePreview: false,
                immediate: false,
                allowMultiple: true,
              },
                            "accepted-file-types": "image/jpeg, image/png"
                          }, null, 512)
                        ]),
                        _: 1
                      }, 8, ["label"]))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["fields"]))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      (_unref(annotationImages) && _unref(annotationImages).length)
        ? (_openBlock(), _createBlock(_unref(NkGallery), {
            key: 0,
            hidden: "",
            ref_key: "annotationGallery",
            ref: annotationGallery
          }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(annotationImages), (image, i) => {
                return (_openBlock(), _createElementBlock("a", {
                  key: image.id,
                  href: _unref(getDomainPath)(image.uri)
                }, [
                  _createElementVNode("img", {
                    src: _unref(getDomainPath)(image.uri),
                    alt: `Image ${i}`
                  }, null, 8, _hoisted_10)
                ], 8, _hoisted_9))
              }), 128))
            ]),
            _: 1
          }, 512))
        : _createCommentVNode("", true),
      (_ctx.$store.getters['connection/isGranted'](['ROLE_ADMIN']))
        ? (_openBlock(), _createBlock(_unref(NkModal), {
            key: 1,
            class: "max-w-screen-lg",
            "model-value": annotationHistoryOpen.value,
            title: `Annotation History (Blade ${_unref(annotation)?.parentDeviceName} / Side ${_unref(annotation)?.deviceName} / Inspection ${__props.inspection})`,
            onClose: closeHistory
          }, {
            default: _withCtx(() => [
              _createVNode(AnnotationHistoryViewer, {
                annotation: props.annotation
              }, null, 8, ["annotation"])
            ]),
            _: 1
          }, 8, ["model-value", "title"]))
        : _createCommentVNode("", true)
    ])), [
      [_directive_loading, _unref(updateAnnotationIsLoading)]
    ])
  ], 2))
}
}

})